/**
 * /equipment/preuse-templates — structured pre-use forms per equipment type
 * (api#1209), full width. A row opens the template on its own page
 * (PreuseTemplateDetailScreen); no panel opens beside the list.
 *
 * CROSS-VERTICAL, no `hasVertical` guard — Equipment is cross-vertical, and so
 * is the phone's pre-use check. A tenant with no live template (every casing
 * tenant today) sees no change on the phone. Casing's own inspection forms
 * (equipment_inspection_templates) are a different system and not touched.
 *
 * The way in is a band button on /equipment and a pointer on Settings →
 * Equipment Types, both shown only to the roles the API lets read the list.
 *
 * FILTER CHIPS live in the URL (`?status=`), so the back arrow from a record
 * returns to the list as it was left. THEIR COUNTS ARE A TALLY OF THE LOADED
 * ROWS: `GET /preuse-templates` is unpaginated and no endpoint returns the
 * figures, so the whole register is loaded once and the chips narrow it.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, FileUp, Loader2, Plus } from 'lucide-react';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { canUploadSourceForm } from '../../lib/roles';
import { preuseTargetOf, type PreuseTarget } from '../../lib/preuse-extraction';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useActiveEquipmentTypes, usePreuseTemplates } from '../../hooks/usePreuseTemplates';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstantDay } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWritePreuse, PREUSE_STATUS_FILL, PREUSE_STATUS_LABEL, PREUSE_STATUS_RAIL } from '../../lib/preuse';
import { PREUSE_TEMPLATE_STATUSES, type PreuseTemplate } from '../../types/preuse';
import { DH_BAND_SECONDARY, DhEmpty, DhPanel } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

export const PREUSE_LIST = '/equipment/preuse-templates';

/** Status badge — saturated fill, dark ink. An unknown status reads `—` on slate, never a real state. */
export function PreuseStatusBadge({ status }: { status: string | null | undefined }) {
  const label = (status && PREUSE_STATUS_LABEL[status]) || '—';
  const fill = (status && PREUSE_STATUS_FILL[status]) || 'bg-fi-signal-slate';
  return (
    <span className={`inline-flex w-fit whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${fill}`}>
      {label}
    </span>
  );
}

/** "Excavator" / "no equipment type" — a template's type may have been deleted out from under it. */
export const typeName = (t: Pick<PreuseTemplate, 'equipmentTypeName'>) => t.equipmentTypeName ?? '—';

/** The "go make an equipment type first" notice — shown wherever the type list is empty. */
export function NoEquipmentTypesNotice() {
  return (
    <p className="px-4 py-6 text-sm text-fieldiq-text" data-testid="preuse-no-types">
      This company has no equipment types yet. A pre-use template is written for one equipment type, so add the types first in{' '}
      <Link to="/settings/equipment-types" className="font-semibold underline underline-offset-2">
        Settings → Equipment Types
      </Link>
      .
    </p>
  );
}

const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' },
  ...PREUSE_TEMPLATE_STATUSES.map((s) => ({ key: s, label: PREUSE_STATUS_LABEL[s] })),
];

/** A `GET /forms/extractions` row. Only a pre-use run carries `preuse_target`. */
interface PendingRunRow {
  id: string;
  status: string;
  original_filename: string | null;
  created_at: string | null;
  preuse_target?: PreuseTarget;
}

/**
 * Pre-use checklists the AI has read and nobody has approved yet (api#1210).
 * Without this a run whose upload page was closed mid-read could be found only
 * from the safety-forms page. Absent when there are none; a failed read says so.
 * Same query key and fetch as the safety-forms "awaiting review" list.
 */
function AwaitingReviewPanel({ zone }: { zone: string | null }) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['form-extractions-pending'],
    queryFn: async () => unwrapArray<PendingRunRow>((await api.forms.extractions({ status: 'needs_review' })).data),
  });
  const failure = queryLoadFailure(query);
  const rows = (query.data ?? []).filter((r) => preuseTargetOf(r));
  if (query.isLoading) return null;
  if (!failure && rows.length === 0) return null;
  return (
    <DhPanel eyebrow="Built from a document" title="Awaiting review" testId="preuse-awaiting-review">
      {failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="checklists awaiting review" />
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => navigate(`${PREUSE_LIST}/extractions/${r.id}`)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-fieldiq-dark/50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-extrabold text-white">{r.original_filename ?? '—'}</span>
                  <span className="block text-xs text-fieldiq-muted">
                    For {r.preuse_target?.equipment_type_name ?? '—'} · read {formatInstantDay(r.created_at, zone) ?? '—'}
                  </span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-fieldiq-muted" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </DhPanel>
  );
}

export default function PreuseTemplatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canWrite = canWritePreuse(user?.role, elevated);
  // The replicator's upload and PATCH routes: admin and office staff (lib/roles).
  const canUpload = canUploadSourceForm(user?.role);
  const zone = useTenantTimeZone();
  const statusParam = params.get('status');
  const active = statusParam && FILTERS.some((f) => f.key === statusParam) ? statusParam : 'all';

  const query = usePreuseTemplates();
  const types = useActiveEquipmentTypes();
  const failure = queryLoadFailure(query);
  const all = query.data ?? [];
  const rows = active === 'all' ? all : all.filter((t) => t.status === active);
  const tally = (key: string) => (key === 'all' ? all.length : all.filter((t) => t.status === key).length);

  const open = (id: string) => openRecord(`${PREUSE_LIST}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const noTypes = !types.isLoading && !types.isError && (types.data ?? []).length === 0;

  return (
    <div data-testid="preuse-templates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Equipment"
        title="Pre-use templates"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite || canUpload ? (
            <>
              {canUpload && (
                <Link to={`${PREUSE_LIST}/from-document`} className={DH_BAND_SECONDARY}>
                  <FileUp className="size-4" aria-hidden="true" />
                  Build from a document
                </Link>
              )}
              {canWrite && (
                <Link to={`${PREUSE_LIST}/new`} className="fi-action fi-action--standard fi-action--primary">
                  <Plus className="size-4" aria-hidden="true" />
                  New template
                </Link>
              )}
            </>
          ) : undefined
        }
        /* No stat strip: the chips below carry every count a strip would. */
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {canUpload && <AwaitingReviewPanel zone={zone} />}

          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={active === f.key}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  if (f.key === 'all') next.delete('status');
                  else next.set('status', f.key);
                  setParams(next, { replace: true });
                }}
                className={`inline-flex items-center gap-2 rounded-fi-pill border px-3 py-1.5 text-sm font-bold transition ${
                  active === f.key ? 'border-fi-amber bg-fi-signal-amber text-fi-signal-ink' : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/60'
                }`}
              >
                {f.label}
                {!query.isLoading && !failure && <span className="font-mono text-xs tabular-nums">{tally(f.key)}</span>}
              </button>
            ))}
          </div>

          <DhPanel
            eyebrow="One form per equipment type"
            title="Templates"
            testId="preuse-template-register"
            footer={!query.isLoading && !failure && all.length > 0 ? `Showing ${rows.length} of ${all.length} · only an admin can publish or archive` : undefined}
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading templates" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="pre-use templates" />
            ) : all.length === 0 ? (
              noTypes ? (
                <NoEquipmentTypesNotice />
              ) : (
                <DhEmpty>
                  There are no pre-use templates yet.
                  {canWrite && ' Start one from a starter list, or from a blank form, with New template.'}
                </DhEmpty>
              )
            ) : rows.length === 0 ? (
              <DhEmpty>No template is {PREUSE_STATUS_LABEL[active]?.toLowerCase() ?? active}.</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[820px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Name</th>
                        <th scope="col" className="px-4 py-3">Equipment type</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3 text-right">Version</th>
                        <th scope="col" className="px-4 py-3 text-right">Items</th>
                        <th scope="col" className="px-4 py-3 text-right">Critical</th>
                        <th scope="col" className="px-4 py-3">Published</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => (
                        <tr
                          key={t.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(t.id)}
                          onKeyDown={rowKeys(t.id)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${PREUSE_STATUS_RAIL[t.status] ?? 'border-l-transparent'}`}
                        >
                          <td className="px-4 py-3 text-sm font-extrabold text-white">{t.name}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{typeName(t)}</td>
                          <td className="px-4 py-3"><PreuseStatusBadge status={t.status} /></td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{t.version === null ? '—' : `v${t.version}`}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{displayValue(t.itemCount)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{displayValue(t.criticalCount)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(t.publishedAt, zone) ?? '—'}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={rowKeys(t.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-white">
                        {t.name}
                        <PreuseStatusBadge status={t.status} />
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {typeName(t)} · {t.version === null ? 'not published' : `v${t.version}`}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-text">
                        {displayValue(t.itemCount)} items · {displayValue(t.criticalCount)} critical
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
